import Settings from "../../utils/CarrotConfig"
import { IOSMobileBridgeClient } from "./mobileNativeClients/IOSMobileBridgeClient"
import { AndroidMobileBridgeClient } from "./mobileNativeClients/AndroidMobileBridgeClient"
import { NullMobileBridgeClient } from "./mobileNativeClients/NullMobileBridgeClient"
import semver from "semver"

const isIOSNativeApp = () => IOSMobileBridgeClient.isIOSMobileClient()
const isAndroidNativeApp = () => AndroidMobileBridgeClient.isAndroidMobileClient()
const isMobileApp = () => isIOSNativeApp() || isAndroidNativeApp()
const isBiometricsCapable = async () => await getBridge().isBiometricsCapable()
const isBiometricLoginAlreadyConfigured = () => getBridge().isBiometricLoginAlreadyConfigured()
const disableBiometrics = () => getBridge().disableBiometrics()

const targetAppVersion = isIOSNativeApp() ? Settings.IOS_TARGET_APP_VERSION : Settings.ANDROID_TARGET_APP_VERSION
const PRE_GETAPPVERSION_SUPPORT_VERSION = isIOSNativeApp() ? "1.0.4" : "1.0.2"

const isAppOutdated = async () => {
  let appVersion

  try {
    appVersion = await getBridge().getAppVersion()
  } catch (error) {
    appVersion = PRE_GETAPPVERSION_SUPPORT_VERSION
  }

  return semver.lt(appVersion ?? PRE_GETAPPVERSION_SUPPORT_VERSION, targetAppVersion)
}

const isPushPermissionsEnabled = async () => {
  try {
    return await getBridge().isPushPermissionsEnabled()
  } catch (error) {
    return null
  }
}

const isMobileAndSupportsPushNotifications = async () => {
  const permissionsEnabled = await isPushPermissionsEnabled()
  return isMobileApp() && typeof permissionsEnabled === "boolean"
}

const promptPushPermissions = () => getBridge().promptPushPermissions()

const setMobileNativeUserInfo = (email: string, employeeId: number) => {
  try {
    getBridge().setUserInfo(email, employeeId)
  } catch (error) {
    // Do nothing
  }
}

const IOS_APP_STORE_URL = "https://apps.apple.com/us/app/carrot-fertility/id1639608799"
const ANDROID_APP_STORE_URL = "https://play.google.com/store/apps/details?id=com.getcarrot.mobile&hl=en_US&pli=1"
const APP_STORE_URL = isIOSNativeApp() ? IOS_APP_STORE_URL : ANDROID_APP_STORE_URL

const attemptBiometricsCredentialsRetrieval = async (login: any) =>
  await getBridge().attemptBiometricsCredentialsRetrieval(login)
const showBiometricsEnrollment = (
  username: any,
  password: any,
  biometricEnrollmentSuccess: any,
  biometricEnrollmentFailure: any
) => getBridge().showBiometricsEnrollment(username, password, biometricEnrollmentSuccess, biometricEnrollmentFailure)

const IfMobileNative = ({ children }: any) => {
  if (!isMobileApp()) {
    return null
  }

  return children
}

const isMobileNativeAndBiometricsCapable = async () => isMobileApp() && (await isBiometricsCapable())

const shouldAttemptBioAuthenticationLogin = async () =>
  isMobileApp() && (await isBiometricsCapable()) && (await isBiometricLoginAlreadyConfigured())

const getBridge = () => {
  if (!isMobileApp()) return new NullMobileBridgeClient()

  return isIOSNativeApp() ? new IOSMobileBridgeClient() : new AndroidMobileBridgeClient()
}

export {
  isMobileApp,
  isIOSNativeApp,
  isAndroidNativeApp,
  IfMobileNative,
  isBiometricsCapable,
  isBiometricLoginAlreadyConfigured,
  disableBiometrics,
  attemptBiometricsCredentialsRetrieval,
  showBiometricsEnrollment,
  shouldAttemptBioAuthenticationLogin,
  isMobileNativeAndBiometricsCapable,
  isPushPermissionsEnabled,
  isAppOutdated,
  promptPushPermissions,
  isMobileAndSupportsPushNotifications,
  setMobileNativeUserInfo,
  APP_STORE_URL
}
